import { fromOADate, getHeaderToIndexMap, getUsedRangeWithProperties, getRowsWithProperties } from "../utils/excel";
import { ExcelValidationError, NoSheetExistsError } from "../utils/ExcelValidationError";
import {
  END_DATE_COLUMN,
  JURISDICTION_ABV_COLUMN,
  STATE_DATE_COLUMN,
  STH_DATES_REQUIRED_COLUMNS,
  STH_TYPE_COLUMN,
} from "../utils/constants";

export interface SthDate {
  readonly sthType: string;
  readonly jurisdiction: string;
  readonly stateDate: Date;
  readonly endDate: Date;
}

export const parseSthDates = (worksheetName: string, setLoadingText: (loadingText: string) => void) => {
  return Excel.run(async (context) => {
    setLoadingText("Reading STH Dates...");

    const sthDatesSheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();

    if (sthDatesSheet.isNullObject) {
      throw NoSheetExistsError(worksheetName);
    }

    const usedRange = await getUsedRangeWithProperties(context, sthDatesSheet, ["rowCount", "columnCount"]);

    const headerToIndex = await getHeaderToIndexMap(usedRange, context, worksheetName);
    for (const column of STH_DATES_REQUIRED_COLUMNS) {
      if (!headerToIndex.has(column)) {
        throw new ExcelValidationError(`Missing column: ${column}`, worksheetName);
      }
    }

    const sthDates: SthDate[] = [];
    const rows = await getRowsWithProperties(usedRange, context, ["values", "valueTypes"]);
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];

      const jurisdiction = row.values[0][headerToIndex.get(JURISDICTION_ABV_COLUMN)].toString().trim();
      const sthType = row.values[0][headerToIndex.get(STH_TYPE_COLUMN)].toString().trim();
      const stateDateOaDate = row.values[0][headerToIndex.get(STATE_DATE_COLUMN)];
      const stateDateOaDateType = row.valueTypes[0][headerToIndex.get(STATE_DATE_COLUMN)];
      const endDateOaDate = row.values[0][headerToIndex.get(END_DATE_COLUMN)];
      const endDateOaDateType = row.valueTypes[0][headerToIndex.get(END_DATE_COLUMN)];

      if (stateDateOaDateType !== "Double" || endDateOaDateType !== "Double") {
        throw new ExcelValidationError("Invalid date format, make sure cell type is Date", worksheetName, i + 1);
      }
      const stateDate = fromOADate(stateDateOaDate);
      const endDate = fromOADate(endDateOaDate);
      if (stateDate > endDate) {
        throw new ExcelValidationError("Start date later than end date", worksheetName, i + 1);
      }

      sthDates.push({
        jurisdiction,
        sthType,
        stateDate,
        endDate,
      });
    }
    return sthDates;
  });
};
